import { Alert, Button, FileInput, Group, Stack } from '@mantine/core';
import { IconDownload, IconFileSpreadsheet, IconUpload } from '@tabler/icons-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Panel } from '../../Panel.js';
import { useLibraryErrorText } from './LibraryTables.js';
import classes from './PmsLibrary.module.css';
import type { PmsLibrarySource } from './source.js';
import type { LibraryImportIssue, LibraryImportResult } from './types.js';

type Origin = 'file' | 'template';

/**
 * Import / Export (ADR 0063 §2, §3): Excel önizleme ve içe aktarma (yalnız eksikler), Felix şablonu
 * ve dışa aktarma. Önizleme hiçbir şey yazmaz; hatalı satır varsa içe aktarma onay ister.
 */
export function ImportExportTab({
  source,
  canImport,
  onImported,
}: {
  source: PmsLibrarySource;
  canImport: boolean;
  onImported: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const errorText = useLibraryErrorText();
  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<{ origin: Origin; result: LibraryImportResult } | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const k = (key: string, values: Record<string, unknown> = {}) =>
    t(`pmsLibrary.importer.${key}`, values);

  const run = async (label: string, origin: Origin, preview: boolean) => {
    setBusy(label);
    setError(null);
    setConfirming(false);
    try {
      const next =
        origin === 'template'
          ? await source.importTemplate(preview)
          : await source.importFile(file as File, preview);
      setResult({ origin, result: next });
      if (!preview) await onImported();
    } catch (failure) {
      setError(errorText(failure));
    } finally {
      setBusy(null);
    }
  };

  const doImport = (origin: Origin) => {
    const skipped = result?.origin === origin ? result.result.summary.skipped : 0;
    if (skipped > 0 && !confirming) {
      setConfirming(true);
      return;
    }
    void run(`${origin}-import`, origin, false);
  };

  const exportFile = async () => {
    setBusy('export');
    setError(null);
    try {
      await source.exportFile();
    } catch (failure) {
      setError(errorText(failure));
    } finally {
      setBusy(null);
    }
  };

  const summary = result?.result.summary;
  return (
    <div className={classes.ioGrid}>
      <Panel title={k('title')} icon={<IconFileSpreadsheet size={18} />}>
        <div className={classes.ioBody}>
          <p className={classes.ioText}>{k('description')}</p>
          <FileInput
            label={k('file')}
            placeholder={k('filePlaceholder')}
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            clearable
            value={file}
            onChange={(next) => {
              setFile(next);
              setResult(null);
              setConfirming(false);
            }}
            disabled={!canImport}
          />
          <Group>
            <Button
              variant="default"
              disabled={!canImport || file === null}
              loading={busy === 'file-preview'}
              onClick={() => void run('file-preview', 'file', true)}
            >
              {t('pmsLibrary.actions.preview')}
            </Button>
            <Button
              leftSection={<IconUpload size={16} />}
              disabled={!canImport || file === null || result?.origin !== 'file'}
              loading={busy === 'file-import'}
              onClick={() => doImport('file')}
            >
              {t('pmsLibrary.actions.import')}
            </Button>
          </Group>
          {error ? <Alert color="red">{error}</Alert> : null}
          {confirming && summary ? (
            <Alert color="yellow">
              {k('confirmWithErrors', { count: summary.skipped })}
              <Group mt="sm">
                <Button
                  size="xs"
                  onClick={() =>
                    void run(`${result?.origin}-import`, result?.origin ?? 'file', false)
                  }
                >
                  {t('pmsLibrary.actions.import')}
                </Button>
                <Button size="xs" variant="default" onClick={() => setConfirming(false)}>
                  {t('pmsLibrary.actions.cancel')}
                </Button>
              </Group>
            </Alert>
          ) : null}
          {summary ? <ImportSummary result={result.result} /> : null}
        </div>
      </Panel>
      <div className={classes.ioSide}>
        <Panel title={k('templateTitle')}>
          <div className={classes.ioBody}>
            <p className={classes.ioText}>{k('templateText')}</p>
            <Group>
              <Button
                variant="default"
                disabled={!canImport}
                loading={busy === 'template-preview'}
                onClick={() => void run('template-preview', 'template', true)}
              >
                {t('pmsLibrary.actions.previewTemplate')}
              </Button>
              <Button
                disabled={!canImport}
                loading={busy === 'template-import'}
                onClick={() => doImport('template')}
              >
                {t('pmsLibrary.actions.loadTemplate')}
              </Button>
            </Group>
          </div>
        </Panel>
        <Panel title={k('exportTitle')}>
          <div className={classes.ioBody}>
            <p className={classes.ioText}>{k('exportText')}</p>
            <Group>
              <Button
                variant="default"
                leftSection={<IconDownload size={16} />}
                loading={busy === 'export'}
                onClick={() => void exportFile()}
              >
                {t('pmsLibrary.actions.export')}
              </Button>
            </Group>
          </div>
        </Panel>
      </div>
    </div>
  );
}

function ImportSummary({ result }: { result: LibraryImportResult }) {
  const { t } = useTranslation();
  const k = (key: string, values: Record<string, unknown> = {}) =>
    t(`pmsLibrary.importer.${key}`, values);
  const { summary } = result;
  const kinds = ['systems', 'items', 'jobs'] as const;
  return (
    <Stack gap="sm" role="region" aria-label={k('summary')}>
      <Alert color={result.preview ? 'blue' : 'green'}>
        {result.preview ? k('previewNote') : k('importedNote')}
        {summary.skipped > 0 ? ` ${k('skippedRows', { count: summary.skipped })}` : ''}
      </Alert>
      <table className={classes.summaryTable}>
        <thead>
          <tr>
            <th scope="col">{k('summary')}</th>
            <th scope="col" data-numeric>
              {result.preview ? k('added') : k('addedDone')}
            </th>
            <th scope="col" data-numeric>
              {k('unchanged')}
            </th>
            <th scope="col" data-numeric>
              {k('differs')}
            </th>
            <th scope="col" data-numeric>
              {k('deleted')}
            </th>
          </tr>
        </thead>
        <tbody>
          {kinds.map((kind) => (
            <tr key={kind}>
              <th scope="row">{k(`kinds.${kind}`)}</th>
              <td data-numeric>{summary[kind].added}</td>
              <td data-numeric>{summary[kind].unchanged}</td>
              <td data-numeric>{summary[kind].differs}</td>
              <td data-numeric>{summary[kind].deleted}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {summary.differs.length > 0 ? (
        <p className={classes.ioText}>{k('differsList', { codes: summary.differs.join(', ') })}</p>
      ) : null}
      <IssueTable title={k('errors')} issues={summary.errors} />
      <IssueTable title={k('warnings')} issues={summary.warnings} />
    </Stack>
  );
}

function IssueTable({ title, issues }: { title: string; issues: LibraryImportIssue[] }) {
  const { t } = useTranslation();
  if (issues.length === 0) return null;
  const k = (key: string) => t(`pmsLibrary.columns.${key}`);
  return (
    <section className={classes.issueList} aria-label={title}>
      <table className={classes.summaryTable}>
        <caption className={classes.muted} style={{ textAlign: 'left', paddingBottom: 4 }}>
          {title} ({issues.length})
        </caption>
        <thead>
          <tr>
            <th scope="col">{k('sheet')}</th>
            <th scope="col" data-numeric>
              {k('row')}
            </th>
            <th scope="col">{k('field')}</th>
            <th scope="col">{k('problem')}</th>
          </tr>
        </thead>
        <tbody>
          {issues.slice(0, 200).map((issue) => (
            <tr key={`${issue.sheet}-${issue.row}-${issue.field}-${issue.code}`}>
              <td>{t(`pmsLibrary.importer.sheets.${issue.sheet}`)}</td>
              <td data-numeric>{issue.row}</td>
              <td>{issue.field}</td>
              <td>{t(`pmsLibrary.issues.${issue.code}`, { defaultValue: issue.code })}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
